/**
 * ScrapeKing Design System v2 - Card Components
 * "Data Extraction Terminal" Aesthetic
 */

import React, { FC, HTMLAttributes } from 'react';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
`;

const countUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// ═══════════════════════════════════════════════════════════════
// BASE CARD
// ═══════════════════════════════════════════════════════════════

const StyledCard = styled.div<{ $variant?: 'default' | 'elevated' | 'bordered'; $interactive?: boolean }>`
  background: var(--sk-surface-default);
  border-radius: var(--sk-radius-lg);
  overflow: hidden;
  position: relative;

  ${props => props.$variant === 'default' && css`
    border: 1px solid var(--sk-surface-border);
  `}

  ${props => props.$variant === 'elevated' && css`
    border: 1px solid var(--sk-surface-border);
    box-shadow: var(--sk-shadow-lg);
  `}

  ${props => props.$variant === 'bordered' && css`
    border: 1px solid var(--sk-surface-border);
    background: transparent;
  `}

  ${props => props.$interactive && css`
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      border-color: var(--sk-surface-border-hover);
      transform: translateY(-2px);
      box-shadow: var(--sk-shadow-lg), 0 0 30px rgba(0, 200, 224, 0.1);
    }

    &:active {
      transform: translateY(0);
    }
  `}

  /* Top accent line */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      var(--sk-cyan-700) 30%,
      var(--sk-cyan-500) 50%,
      var(--sk-cyan-700) 70%,
      transparent 100%
    );
    opacity: 0.5;
  }
`;

const CardHeader = styled.div`
  padding: var(--sk-space-5) var(--sk-space-6);
  border-bottom: 1px solid var(--sk-surface-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sk-space-4);
`;

const CardTitle = styled.h3`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-base);
  font-weight: 600;
  color: var(--sk-text-primary);
  margin: 0;
  letter-spacing: -0.01em;
`;

const CardSubtitle = styled.p`
  font-size: var(--sk-text-sm);
  color: var(--sk-text-secondary);
  margin: var(--sk-space-1) 0 0;
`;

const CardBody = styled.div`
  padding: var(--sk-space-6);
`;

const CardFooter = styled.div`
  padding: var(--sk-space-4) var(--sk-space-6);
  border-top: 1px solid var(--sk-surface-border);
  background: var(--sk-bg-secondary);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--sk-space-3);
`;

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'bordered';
  interactive?: boolean;
}

export const Card: FC<CardProps> & {
  Header: typeof CardHeader;
  Title: typeof CardTitle;
  Subtitle: typeof CardSubtitle;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
} = ({ variant = 'default', interactive = false, children, ...props }) => {
  return (
    <StyledCard $variant={variant} $interactive={interactive} {...props}>
      {children}
    </StyledCard>
  );
};

Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Subtitle = CardSubtitle;
Card.Body = CardBody;
Card.Footer = CardFooter;

// ═══════════════════════════════════════════════════════════════
// STAT CARD (for metrics/KPIs)
// ═══════════════════════════════════════════════════════════════

type StatVariant = 'default' | 'cyan' | 'green' | 'amber' | 'red';

const statAccentColors = {
  default: css`
    --accent-color: var(--sk-cyan-500);
    --accent-glow: var(--sk-cyan-glow);
    --accent-bg: rgba(0, 200, 224, 0.1);
  `,
  cyan: css`
    --accent-color: var(--sk-cyan-500);
    --accent-glow: var(--sk-cyan-glow);
    --accent-bg: rgba(0, 200, 224, 0.1);
  `,
  green: css`
    --accent-color: var(--sk-green-500);
    --accent-glow: var(--sk-green-glow);
    --accent-bg: rgba(0, 255, 127, 0.1);
  `,
  amber: css`
    --accent-color: var(--sk-amber-500);
    --accent-glow: var(--sk-amber-glow);
    --accent-bg: rgba(255, 184, 0, 0.1);
  `,
  red: css`
    --accent-color: var(--sk-red-500);
    --accent-glow: var(--sk-red-glow);
    --accent-bg: rgba(255, 0, 51, 0.1);
  `,
};

const StatCardWrapper = styled.div<{ $variant: StatVariant }>`
  ${props => statAccentColors[props.$variant]}

  background: var(--sk-surface-default);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-lg);
  padding: var(--sk-space-5);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  /* Accent glow on left edge */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: var(--accent-color);
    box-shadow: 0 0 20px var(--accent-glow);
  }

  /* Corner grid decoration */
  &::after {
    content: '';
    position: absolute;
    top: -20px;
    right: -20px;
    width: 80px;
    height: 80px;
    background-image:
      linear-gradient(var(--accent-color) 1px, transparent 1px),
      linear-gradient(90deg, var(--accent-color) 1px, transparent 1px);
    background-size: 10px 10px;
    opacity: 0.1;
    border-radius: 50%;
    transform: rotate(15deg);
  }

  &:hover {
    border-color: var(--sk-surface-border-hover);
    transform: translateY(-2px);
  }
`;

const StatIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: var(--sk-radius-default);
  background: var(--accent-bg);
  border: 1px solid var(--accent-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-color);
  margin-bottom: var(--sk-space-4);

  svg {
    width: 22px;
    height: 22px;
  }
`;

const StatLabel = styled.div`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-2xs);
  font-weight: 500;
  color: var(--sk-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: var(--sk-space-2);
`;

const StatValue = styled.div`
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-3xl);
  font-weight: 700;
  color: var(--sk-text-primary);
  letter-spacing: -0.02em;
  line-height: 1;
  animation: ${countUp} 0.5s ease-out;
`;

const StatChange = styled.div<{ $positive?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--sk-space-1);
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-xs);
  font-weight: 500;
  color: ${props => props.$positive ? 'var(--sk-green-500)' : 'var(--sk-red-500)'};
  margin-top: var(--sk-space-3);
  padding: var(--sk-space-1) var(--sk-space-2);
  background: ${props => props.$positive ? 'rgba(0, 255, 127, 0.1)' : 'rgba(255, 0, 51, 0.1)'};
  border-radius: var(--sk-radius-sm);

  svg {
    width: 12px;
    height: 12px;
  }
`;

const TrendUpSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23,6 13.5,15.5 8.5,10.5 1,18" />
    <polyline points="17,6 23,6 23,12" />
  </svg>
);

const TrendDownSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23,18 13.5,8.5 8.5,13.5 1,6" />
    <polyline points="17,18 23,18 23,12" />
  </svg>
);

interface StatCardProps {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  change?: number;
  variant?: StatVariant;
}

export const StatCard: FC<StatCardProps> = ({
  label,
  value,
  icon,
  change,
  variant = 'default',
}) => {
  const isPositive = change !== undefined && change >= 0;

  return (
    <StatCardWrapper $variant={variant}>
      {icon && <StatIcon>{icon}</StatIcon>}
      <StatLabel>{label}</StatLabel>
      <StatValue>{value}</StatValue>
      {change !== undefined && (
        <StatChange $positive={isPositive}>
          {isPositive ? <TrendUpSvg /> : <TrendDownSvg />}
          {isPositive ? '+' : ''}{change}%
        </StatChange>
      )}
    </StatCardWrapper>
  );
};

// ═══════════════════════════════════════════════════════════════
// STATUS CARD (for showing live status)
// ═══════════════════════════════════════════════════════════════

const StatusDot = styled.div<{ $status: 'active' | 'warning' | 'error' | 'idle' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.$status) {
      case 'active': return 'var(--sk-green-500)';
      case 'warning': return 'var(--sk-amber-500)';
      case 'error': return 'var(--sk-red-500)';
      default: return 'var(--sk-text-tertiary)';
    }
  }};
  box-shadow: ${props => {
    switch (props.$status) {
      case 'active': return '0 0 10px var(--sk-green-glow)';
      case 'warning': return '0 0 10px var(--sk-amber-glow)';
      case 'error': return '0 0 10px var(--sk-red-glow)';
      default: return 'none';
    }
  }};

  ${props => props.$status !== 'idle' && css`
    animation: ${pulseGlow} 2s ease-in-out infinite;
  `}
`;

const StatusCardWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sk-space-3);
  padding: var(--sk-space-3) var(--sk-space-4);
  background: var(--sk-bg-secondary);
  border: 1px solid var(--sk-surface-border);
  border-radius: var(--sk-radius-default);
`;

const StatusText = styled.span`
  font-family: var(--sk-font-mono);
  font-size: var(--sk-text-sm);
  color: var(--sk-text-secondary);
`;

interface StatusIndicatorProps {
  status: 'active' | 'warning' | 'error' | 'idle';
  text: string;
}

export const StatusIndicator: FC<StatusIndicatorProps> = ({ status, text }) => (
  <StatusCardWrapper>
    <StatusDot $status={status} />
    <StatusText>{text}</StatusText>
  </StatusCardWrapper>
);

// ═══════════════════════════════════════════════════════════════
// DATA TABLE CARD
// ═══════════════════════════════════════════════════════════════

const TableCard = styled(StyledCard)`
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--sk-bg-secondary);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--sk-surface-border);
    border-radius: var(--sk-radius-full);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--sk-text-sm);
`;

const TableHead = styled.thead`
  background: var(--sk-bg-secondary);
  border-bottom: 1px solid var(--sk-surface-border);
`;

const TableHeadCell = styled.th`
  padding: var(--sk-space-3) var(--sk-space-4);
  text-align: left;
  font-family: var(--sk-font-display);
  font-size: var(--sk-text-xs);
  font-weight: 600;
  color: var(--sk-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $clickable?: boolean }>`
  border-bottom: 1px solid var(--sk-surface-border);
  transition: background 0.2s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--sk-surface-hover);
  }
`;

const TableCell = styled.td`
  padding: var(--sk-space-4);
  color: var(--sk-text-primary);
  vertical-align: middle;
`;

export const DataTable = {
  Card: TableCard,
  Wrapper: TableWrapper,
  Table,
  Head: TableHead,
  HeadCell: TableHeadCell,
  Body: TableBody,
  Row: TableRow,
  Cell: TableCell,
};

export default Card;
